import { Alert, Box, Grid2 as Grid, Snackbar } from '@mui/material'
import SimpleTitle from 'components/SimpleTitle'
import useAuth from 'hooks/useAuth'
import { usePeriods } from 'hooks/usePeriods'
import { useFetchRewardPackages } from 'hooks/useRewardPackages'
import type { FC } from 'react'
import React, { useEffect, useState } from 'react'
import type { Period } from 'types/periods'
import { createIdToPeriodFromList } from 'types/periods'
import DownloadSection from './DownloadSection'
import type { FormType, SnackbarState } from './types'
import UploadSection from './UploadSection'

const Forms: FC = () => {
  const { token, profile } = useAuth()

  // Download form state
  const [downloadFormType, setDownloadFormType] = useState<FormType | ''>('')
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([])
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([])
  const [rootPeriodId, setRootPeriodId] = useState<string>('')
  const [selectedRootPeriodId, setSelectedRootPeriodId] = useState<string>('')
  const [selectedPackageId, setSelectedPackageId] = useState<string>('')
  const [periods, setPeriods] = useState<Period[]>([])
  const [periodMap, setPeriodMap] = useState<{ [key: string]: Period }>({})

  // Upload form state
  const [uploadFormType, setUploadFormType] = useState<FormType | ''>('')
  const [file, setFile] = useState<File | null>(null)

  // Shared state
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Fetch periods - needed when organization is selected OR when using rootPeriod selector (import-user-rewards)
  const needsPeriods = selectedOrganizations.length > 0 || downloadFormType === 'import-user-rewards'
  const { periods: fetchedPeriods, isLoadingPeriods } = usePeriods(
    token!,
    rootOrganizationId,
    !!token && needsPeriods && rootOrganizationId !== ''
  )

  const { packages: rewardPackages, isLoading: isLoadingPackages } = useFetchRewardPackages(
    rootOrganizationId,
    token!,
    !!token && rootOrganizationId !== '' && downloadFormType === 'import-user-rewards'
  )

  useEffect(() => {
    if (fetchedPeriods) {
      setPeriods(fetchedPeriods.periods || [])
      if (fetchedPeriods.periods.length > 0) {
        setRootPeriodId(fetchedPeriods.periods[0].id!)
        setPeriodMap(createIdToPeriodFromList(fetchedPeriods.periods))
      }
    }
  }, [fetchedPeriods])

  useEffect(() => {
    if (profile && profile.roles && profile.roles.length > 0 && rootOrganizationId === '') {
      const orgId = profile.roles[0].organization.rootOrganizationId
      if (orgId) {
        setRootOrganizationId(orgId)
      }
    }
  }, [profile, rootOrganizationId])

  useEffect(() => {
    if (selectedOrganizations.length === 0) {
      setSelectedPeriods([])
    }
  }, [selectedOrganizations])

  const handleDownloadOrganizationChange = (selected: string[]) => {
    setSelectedPeriods([])
    setSelectedOrganizations(selected)
  }

  const handleDownloadPeriodChange = (selected: string[]) => {
    setSelectedPeriods(selected)
    if (selected.length > 0) {
      const rootPeriodId = periodMap[selected[0]]?.rootPeriodId
      if (rootPeriodId) {
        setRootPeriodId(rootPeriodId)
      }
    }
  }

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackbar({ ...snackbar, open: false })
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 150px)',
      }}
    >
      <SimpleTitle title="Forms" />
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Grid container spacing={4}>
          {/* Download Forms Section */}
          <Grid size={6}>
            <DownloadSection
              profile={profile!}
              token={token!}
              rootOrganizationId={rootOrganizationId}
              formType={downloadFormType}
              selectedOrganizations={selectedOrganizations}
              selectedPeriods={selectedPeriods}
              rootPeriodId={rootPeriodId}
              selectedRootPeriodId={selectedRootPeriodId}
              selectedPackageId={selectedPackageId}
              periods={periods}
              rewardPackages={rewardPackages || []}
              isLoadingPeriods={isLoadingPeriods}
              isLoadingPackages={isLoadingPackages}
              onFormTypeChange={setDownloadFormType}
              onOrganizationChange={handleDownloadOrganizationChange}
              onPeriodChange={handleDownloadPeriodChange}
              onRootPeriodChange={setSelectedRootPeriodId}
              onPackageChange={setSelectedPackageId}
              onSnackbarChange={setSnackbar}
            />
          </Grid>

          {/* Upload Forms Section */}
          <Grid size={6}>
            <UploadSection
              token={token!}
              rootOrganizationId={rootOrganizationId}
              formType={uploadFormType}
              file={file}
              onFormTypeChange={setUploadFormType}
              onFileChange={setFile}
              onSnackbarChange={setSnackbar}
            />
          </Grid>
        </Grid>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
          {snackbar.details && (
            <Box sx={{ mt: 1 }}>
              {Array.isArray(snackbar.details) ? (
                snackbar.details.map((detail, index) => (
                  <Box key={index} component="div" sx={{ mt: 0.5 }}>
                    • {detail}
                  </Box>
                ))
              ) : (
                <Box component="div" sx={{ mt: 0.5 }}>
                  • {snackbar.details}
                </Box>
              )}
            </Box>
          )}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Forms
